<template>
  <FormContainer>
    <template #title>Informations complémentaires</template>
    <template #content>
      <div class="form__bloc">
        <WrapperDropdown
          id="field_138"
          :model-value="salariéEnCours?.natureContrat"
          @update:model-value="(value: any) => updateModule('natureContrat', activeSalariéIndex!, value)"
          label="Nature du contrat"
          class="col-12 col-md-18 col-sm-36"
          :items="lov.NatureContrat"
          :required="metaSalarieEnCours?.natureContrat?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.natureContrat?.$messageErreurDirty!"
          key-id="code"
          key-label="intitule"
          :readonly
        />

        <WrapperDropdown
          id="field_139"
          :model-value="salariéEnCours?.csp"
          @update:model-value="(value: any) => updateModule('csp', activeSalariéIndex!, value)"
          label="Catégorie socio-professionnelle (CSP)"
          class="col-12 col-md-18 col-sm-36"
          :items="lov.Csp"
          :required="metaSalarieEnCours?.csp?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.csp?.$messageErreurDirty!"
          key-id="code"
          key-label="intitule"
          :readonly
        />
        <WrapperDropdown
          id="field_140"
          :model-value="salariéEnCours?.statut"
          @update:model-value="(value: any) => updateModule('statut', activeSalariéIndex!, value)"
          label="Statut salarié"
          class="col-12 col-md-18 col-sm-36"
          :items="statutItems"
          :required="metaSalarieEnCours?.statut?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.statut?.$messageErreurDirty!"
          key-id="code"
          key-label="intitule"
          :readonly
        />
      </div>
      <div class="form__bloc">
        <WrapperDropdown
          id="field_141"
          :model-value="salariéEnCours?.niveauFormation"
          @update:model-value="(value: any) => updateModule('niveauFormation', activeSalariéIndex!, value)"
          label="Niveau actuel de formation"
          class="col-12 col-md-18 col-sm-36"
          :items="lov.NiveauDiplome"
          :required="metaSalarieEnCours?.niveauFormation?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.niveauFormation?.$messageErreurDirty!"
          key-id="code"
          key-label="intitule"
          :readonly
        />
        <WrapperInput
          v-if="metaSalarieEnCours?.numeroDossierCpf?.$status !== PropertyStatus.NotPresent"
          id="field_142"
          :model-value="salariéEnCours?.numeroDossierCpf"
          @update:model-value="(value: any) => updateModule('numeroDossierCpf', activeSalariéIndex!, value)"
          label="Numéro de dossier CPF"
          :required="metaSalarieEnCours?.numeroDossierCpf?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.numeroDossierCpf?.$messageErreurDirty!"
          hint="255 caractères maximum"
          class="col-12 col-md-18 col-sm-36"
          :maxlength="255"
          deletable
          :readonly
        />
        <WrapperInput
          v-else
          id="field_142"
          label="Numéro de dossier CPF"
          hint="255 caractères maximum"
          class="col-12 col-md-18 col-sm-36"
          :readonly
          disabled
        />
      </div>

      <div class="form__bloc">
        <WrapperInputNumber
          id="field_143"
          :model-value="salariéEnCours?.tauxHoraireBrut"
          @update:model-value="(value: any) => updateModule('tauxHoraireBrut', activeSalariéIndex!, value)"
          label="Taux horaire brut"
          :required="metaSalarieEnCours?.tauxHoraireBrut?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.tauxHoraireBrut?.$messageErreurDirty!"
          class="col-6 col-md-19 col-sm-36"
          :decimal="2"
          deletable
          :readonly
        />
        <WrapperInputNumber
          id="field_144"
          :model-value="salariéEnCours?.pctTauxCharge"
          @update:model-value="(value: any) => updateModule('pctTauxCharge', activeSalariéIndex!, value)"
          label="Taux de charge (%)"
          :required="metaSalarieEnCours?.pctTauxCharge?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.pctTauxCharge?.$messageErreurDirty!"
          class="col-10 col-md-17 col-sm-36"
          pourcentage
          :decimal="2"
          deletable
          :readonly
        />
        <WrapperInputNumber
          id="field_145"
          :model-value="total"
          label="Total"
          class="col-10 col-md-18 col-sm-36"
          :decimal="2"
          disabled
          :readonly
          state="disabled"
        />
      </div>
    </template>
  </FormContainer>
</template>

<script setup lang="ts">
  import { computed, inject } from 'vue'
  import { storeToRefs } from 'pinia'
  import { useDemandeAFStore } from '@/features/interface/demande/af/useDemandeAFStore'
  import { SalarieActionKey, type SalarieAction } from '@/features/interface/demande/af/steps/salarie/useSalarieActions'
  import { PropertyStatus } from '@/features/domaine/property-status/PropertyStatus'
  import { FormContainer } from '@/features/interface/demande/shared'
  import type { SalarieDTO } from '@/features/api/extranet/dto/Demande/AF'
  import { calculSalaire } from '@/features/domaine/regles-akto/shared'

  const store = useDemandeAFStore()
  const { setupArrayUpdaterWithKey } = store

  const { lov, readonly, model } = storeToRefs(store)
  const { activeSalariéIndex, salariéEnCours, metaSalarieEnCours } = inject<SalarieAction>(SalarieActionKey)!

  const updateModule = setupArrayUpdaterWithKey<SalarieDTO>('salaries')

  const total = computed(() => {
    const tauxHoraireBrut = salariéEnCours.value?.tauxHoraireBrut || 0
    const tauxCharge = salariéEnCours.value?.pctTauxCharge || 0

    const dureeFormation = model.value?.formation?.modules?.reduce((sum, mod) => sum + (mod.duree ?? 0), 0) ?? 0
    return calculSalaire(tauxHoraireBrut, tauxCharge, dureeFormation)
  })

  const statutItems = computed(() => {
    const statutItems3219 = ['Portage', 'Permanent', 'DemandeurEmploi']
    const items =
      model.value?.employeur?.etablissement?.idcc?.code === '3219'
        ? lov.value.StatutSalarie.filter((x) => statutItems3219.includes(x.code))
        : lov.value.StatutSalarie
    return items
  })
</script>
